import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { Badge } from '../../components/ui/badge';
import { Loader2, Mail, Printer, Paperclip, MessageSquare, MessageSquareReply } from 'lucide-react';

const STATUS = {
  pending: ['Wartet auf Freigabe', 'bg-amber-100 text-amber-700'],
  approved: ['Freigegeben', 'bg-blue-100 text-blue-700'],
  sent: ['Gesendet', 'bg-emerald-100 text-emerald-700'],
  failed: ['Fehlgeschlagen', 'bg-rose-100 text-rose-700'],
  rejected: ['Abgelehnt', 'bg-rose-100 text-rose-700'],
  cancelled: ['Storniert', 'bg-slate-100 text-slate-600'],
  printed: ['Gedruckt', 'bg-brand-red/10 text-brand-red'],
  queued: ['In Warteschlange', 'bg-amber-100 text-amber-700'],
  delivered: ['Zugestellt', 'bg-emerald-100 text-emerald-700'],
  blocked_denylist: ['Blockiert', 'bg-rose-100 text-rose-700'],
  disabled: ['SMS deaktiviert', 'bg-slate-100 text-slate-600'],
};

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–');

export function CorrespondenceHistoryTab({ guardianId, onUnreadChanged }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    if (!guardianId) return;
    (async () => {
      try {
        const r = await api.get(`/guardians/${guardianId}/correspondence-history`);
        if (cancelled) return;
        const data = r.data.items || [];
        setItems(data);
        // Auto-Mark-as-read: alle eingehenden SMS-Antworten als gelesen markieren
        const hasUnread = data.some((it) => it.kind === 'sms' && it.direction === 'inbound' && it.is_unread);
        if (hasUnread) {
          try {
            await api.post(`/guardians/${guardianId}/sms-mark-read`);
            if (onUnreadChanged) onUnreadChanged(0);
          } catch { /* nicht kritisch */ }
        } else if (onUnreadChanged) {
          onUnreadChanged(0);
        }
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [guardianId, onUnreadChanged]);

  if (items === null) {
    return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  }
  if (!items.length) {
    return <div className="text-sm text-slate-400 py-8 text-center" data-testid="correspondence-history-empty">Noch keine Schreiben oder SMS an diese/n Zahler/in versendet</div>;
  }
  return (
    <table className="w-full text-sm" data-testid="correspondence-history-table">
      <thead>
        <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
          <th className="py-2 pr-2 w-32">Datum</th>
          <th className="py-2 pr-2">Betreff / Nachricht</th>
          <th className="py-2 pr-2 w-10"></th>
          <th className="py-2 text-right w-40">Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          const [lbl, cls] = STATUS[it.status] || [it.status, 'bg-slate-100 text-slate-600'];
          const isInbound = it.kind === 'sms' && it.direction === 'inbound';
          const rowHighlight = isInbound && it.is_unread ? 'bg-emerald-50/60' : '';
          return (
            <tr
              key={it.id}
              className={`border-b border-slate-100 last:border-0 ${rowHighlight}`}
              data-testid={`correspondence-history-row-${it.id}`}
            >
              <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap align-top">{fmtDateTime(it.date)}</td>
              <td className="py-1.5 pr-2 align-top">
                <div className="flex items-center gap-1.5 text-slate-900">
                  {it.kind === 'letter'
                    ? <Printer className="h-3.5 w-3.5 text-brand-red shrink-0" title="Gedruckter Brief" />
                    : it.kind === 'sms'
                      ? (isInbound
                          ? <MessageSquareReply className="h-3.5 w-3.5 text-emerald-600 shrink-0" title="Eingehende SMS-Antwort" />
                          : <MessageSquare className="h-3.5 w-3.5 text-teal-500 shrink-0" title="Ausgehende SMS" />)
                      : <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" title="E-Mail" />}
                  <span className={`truncate max-w-md ${isInbound && it.is_unread ? 'font-semibold text-emerald-900' : ''}`}>{it.subject}</span>
                  {isInbound && it.is_unread && (
                    <Badge className="border-0 bg-emerald-600 text-white text-[10px] px-1.5 py-0" data-testid={`correspondence-history-unread-${it.id}`}>Neu</Badge>
                  )}
                </div>
                {isInbound && it.sender_phone && (
                  <div className="text-[11px] text-emerald-700 ml-5">Antwort von {it.sender_phone}</div>
                )}
                {!isInbound && it.source && <div className="text-[11px] text-slate-400 ml-5">{it.source}</div>}
              </td>
              <td className="py-1.5 pr-2 align-top">
                {it.attachments?.length > 0 && (
                  <Paperclip className="h-3.5 w-3.5 text-slate-400" title={it.attachments.join(', ')} />
                )}
              </td>
              <td className="py-1.5 text-right align-top">
                {isInbound
                  ? <Badge className="border-0 bg-emerald-100 text-emerald-800">Antwort erhalten</Badge>
                  : <Badge className={`${cls} border-0`}>{lbl}</Badge>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
